import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button, EmptyState, Spinner } from '../../ui';

/**
 * Handler for `/global/auth/callback`. Runs the Authorization-Code + PKCE token
 * exchange, then navigates home. Registered as a callback URL on the Cognito
 * app client (http://localhost:3000/global/auth/callback for local dev).
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { completeSignIn } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invoke (code is single-use).
    if (ran.current) return;
    ran.current = true;
    completeSignIn()
      .then(() => navigate('/', { replace: true }))
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Sign-in failed'),
      );
  }, [completeSignIn, navigate]);

  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Sign-in didn't complete"
        description={error}
        action={
          <Button onClick={() => navigate('/settings', { replace: true })}>
            Go to Settings
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex items-center justify-center py-24">
      <Spinner label="Completing sign-in" />
    </div>
  );
}

export default AuthCallbackPage;
